import * as React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
    createRewardedPromptCampaign,
    getPresentationScreenRegistry,
    getRewardedPromptCampaign,
    getRewardedPromptTriggerRegistry,
    getUserTags,
    getVideoPlacements,
    updateRewardedPromptCampaign,
} from '../Api';
import UserTagDefinitionDto from '../models/dtos/UserTagDefinitionDto';
import VideoPlacementDto from '../models/dtos/VideoPlacementDto';
import type {
    RewardedPromptCampaignDto,
    RewardedPromptCampaignUpsertRequest,
    RewardedPromptContextFilterDto,
    RewardedPromptContextFilterOp,
    RewardedPromptFrequencyMode,
    RewardedPromptTriggerMatchMode,
} from '../models/dtos/RewardedPromptCampaignDto';
import type { PresentationScreenRegistryDto } from '../models/dtos/PresentationScreenRegistryDto';
import { BuildNumberAutocomplete, useAppVersionsForOsTypes } from '../utils/appVersionFields';
import IconButton from '@mui/material/IconButton';

const FREQUENCY_MODES: RewardedPromptFrequencyMode[] = ['ONE_TIME', 'N_PER_DAY', 'UNLIMITED_WITH_COOLDOWN'];
const TRIGGER_MATCH_MODES: RewardedPromptTriggerMatchMode[] = ['ANY', 'ALL'];
const CONTEXT_FILTER_OPS: RewardedPromptContextFilterOp[] = ['EQ', 'IN', 'EXISTS', 'GTE', 'LTE'];
const DEFAULT_CONTEXT_KEYS = ['match_id', 'sport', 'bet_amount', 'bet_id', 'won', 'profit'];

const emptyFilter = (): RewardedPromptContextFilterDto => ({
    key: 'match_id',
    op: 'IN',
    values: [],
});

const emptyForm = (): RewardedPromptCampaignUpsertRequest => ({
    slug: '',
    name: '',
    enabled: false,
    priority: 0,
    placementId: 'video_auto_prompt',
    triggerEvents: [],
    screenKeys: [],
    triggerMatchMode: 'ANY',
    contextFilters: [],
    userTags: [],
    tourCompletion: 'any',
    osTypes: ['android'],
    minBuild: null,
    maxBuild: null,
    frequencyMode: 'N_PER_DAY',
    maxImpressionsPerUser: 1000,
    maxImpressionsPerDay: 3,
    cooldownMinutes: 60,
    startAt: null,
    endAt: null,
});

const toIsoOrNull = (value: string): string | null => {
    if (!value.trim()) return null;
    return new Date(value).toISOString();
};

const fromIsoToLocalInput = (value: string | null | undefined): string => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const campaignToForm = (campaign: RewardedPromptCampaignDto): RewardedPromptCampaignUpsertRequest => ({
    slug: campaign.slug,
    name: campaign.name,
    enabled: campaign.enabled,
    priority: campaign.priority,
    placementId: campaign.placementId,
    triggerEvents: campaign.triggerEvents,
    screenKeys: campaign.screenKeys,
    triggerMatchMode: campaign.triggerMatchMode ?? 'ANY',
    contextFilters: (campaign.contextFilters ?? []).map(f => ({
        key: f.key,
        op: f.op,
        values: f.values ?? [],
    })),
    userTags: campaign.userTags,
    tourCompletion: campaign.tourCompletion,
    osTypes: campaign.osTypes.length > 0 ? campaign.osTypes : ['android'],
    minBuild: campaign.minBuild,
    maxBuild: campaign.maxBuild,
    frequencyMode: campaign.frequencyMode,
    maxImpressionsPerUser: campaign.maxImpressionsPerUser,
    maxImpressionsPerDay: campaign.maxImpressionsPerDay,
    cooldownMinutes: campaign.cooldownMinutes,
    startAt: campaign.startAt,
    endAt: campaign.endAt,
});

export default function RewardedPromptCampaignEditPage() {
    const id = useParams()['id'] as string | undefined;
    const isEditMode = id != null;
    const navigate = useNavigate();

    const [loading, setLoading] = React.useState(isEditMode);
    const [saving, setSaving] = React.useState(false);
    const [toast, setToast] = React.useState<{ message: string; severity: 'success' | 'error' } | null>(null);
    const [triggerEvents, setTriggerEvents] = React.useState<string[]>([]);
    const [contextKeyOptions, setContextKeyOptions] = React.useState<string[]>(DEFAULT_CONTEXT_KEYS);
    const [userTags, setUserTags] = React.useState<UserTagDefinitionDto[]>([]);
    const [placements, setPlacements] = React.useState<VideoPlacementDto[]>([]);
    const [screenRegistry, setScreenRegistry] = React.useState<PresentationScreenRegistryDto | null>(null);
    const [form, setForm] = React.useState<RewardedPromptCampaignUpsertRequest>(emptyForm());
    const { versions: appVersions, loading: appVersionsLoading } = useAppVersionsForOsTypes(form.osTypes);

    const screenOptions = React.useMemo(() => {
        const screens = screenRegistry?.screens ?? [];
        const keys = screens.map(s => s.screenKey).filter(Boolean);
        const groups = Object.keys(screenRegistry?.groups ?? {});
        return Array.from(new Set([...keys, ...groups]));
    }, [screenRegistry]);

    const showToast = (message: string, severity: 'success' | 'error' = 'success') => {
        setToast({ message, severity });
    };

    React.useEffect(() => {
        const load = async () => {
            setLoading(true);
            const [registry, tags, triggers, videoPlacements, campaign] = await Promise.all([
                getPresentationScreenRegistry(),
                getUserTags(),
                getRewardedPromptTriggerRegistry(),
                getVideoPlacements(),
                isEditMode && id ? getRewardedPromptCampaign(id) : Promise.resolve(null),
            ]);
            setScreenRegistry(registry);
            setUserTags(tags ?? []);
            setTriggerEvents(triggers?.events ?? []);
            setContextKeyOptions(
                Array.from(new Set([...(triggers?.contextKeys ?? []), ...DEFAULT_CONTEXT_KEYS])),
            );
            setPlacements((videoPlacements ?? []).filter(p => p.enabled !== false));
            if (campaign) {
                setForm(campaignToForm(campaign));
            } else if (isEditMode) {
                showToast('Campaign not found.', 'error');
            }
            setLoading(false);
        };
        load();
    }, [id, isEditMode]);

    const handleSave = async () => {
        if (!form.triggerEvents.length && !form.screenKeys.length) {
            showToast('Select at least one trigger event or screen key.', 'error');
            return;
        }
        if (form.maxImpressionsPerDay < 1 || form.maxImpressionsPerDay > 5) {
            showToast('Max dialogs per day must be between 1 and 5.', 'error');
            return;
        }
        setSaving(true);
        const payload: RewardedPromptCampaignUpsertRequest = {
            ...form,
            startAt: form.startAt ? toIsoOrNull(fromIsoToLocalInput(form.startAt)) : null,
            endAt: form.endAt ? toIsoOrNull(fromIsoToLocalInput(form.endAt)) : null,
        };
        const result = isEditMode && id
            ? await updateRewardedPromptCampaign(id, payload)
            : await createRewardedPromptCampaign(payload);
        setSaving(false);
        if (!result.ok) {
            showToast(result.error ?? 'Save failed.', 'error');
            return;
        }
        const saved = result.data;
        setForm(campaignToForm(saved));
        showToast(isEditMode ? 'Campaign updated.' : 'Campaign created.', 'success');
        if (!isEditMode) {
            navigate('/marketing/rewarded-prompts/' + encodeURIComponent(saved.id) + '/edit', { replace: true });
        }
    };

    if (loading) {
        return (
            <Box sx={{ mx: 3, my: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ mx: 3, my: 3 }}>
            <Stack spacing={3}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
                    <Typography variant="h4">
                        {isEditMode ? 'Edit rewarded video prompt' : 'New rewarded video prompt'}
                    </Typography>
                    <Button variant="outlined" onClick={() => navigate('/marketing/rewarded-prompts')}>Back</Button>
                </Stack>

                <Alert severity="info">
                    Dialog UI is native in the Android app. Here you only configure targeting, triggers, and frequency.
                    The dialog appears only when a rewarded video is available for the selected placement.
                </Alert>

                <Card>
                    <CardContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Name"
                                    value={form.name}
                                    onChange={e => setForm({ ...form, name: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Slug"
                                    value={form.slug}
                                    onChange={e => setForm({ ...form, slug: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Video placement</InputLabel>
                                    <Select
                                        label="Video placement"
                                        value={form.placementId}
                                        onChange={e => setForm({ ...form, placementId: e.target.value })}
                                    >
                                        {placements.map(p => (
                                            <MenuItem key={p.placementId} value={p.placementId}>
                                                {p.placementId}
                                            </MenuItem>
                                        ))}
                                        {!placements.some(p => p.placementId === form.placementId) && form.placementId && (
                                            <MenuItem value={form.placementId}>{form.placementId}</MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Priority"
                                    value={form.priority}
                                    onChange={e => setForm({ ...form, priority: Number(e.target.value) || 0 })}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControlLabel
                                    control={<Switch checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} />}
                                    label="Enabled"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    options={triggerEvents}
                                    value={form.triggerEvents}
                                    onChange={(_, value) => setForm({ ...form, triggerEvents: value.map(String) })}
                                    renderInput={(params) => <TextField {...params} label="Trigger events" helperText="Analytics EventType names (e.g. BetPlaced)" />}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    options={screenOptions}
                                    value={form.screenKeys}
                                    onChange={(_, value) => setForm({ ...form, screenKeys: value.map(String) })}
                                    renderInput={(params) => <TextField {...params} label="Screen keys" helperText="From presentation screen registry (e.g. match_details, home)" />}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth disabled={form.triggerEvents.length === 0 || form.screenKeys.length === 0}>
                                    <InputLabel>Event + screen match</InputLabel>
                                    <Select
                                        label="Event + screen match"
                                        value={form.triggerMatchMode}
                                        onChange={e => setForm({
                                            ...form,
                                            triggerMatchMode: e.target.value as RewardedPromptTriggerMatchMode,
                                        })}
                                    >
                                        {TRIGGER_MATCH_MODES.map(mode => (
                                            <MenuItem key={mode} value={mode}>
                                                {mode === 'ANY' ? 'ANY — event OR screen' : 'ALL — event AND screen'}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    Used only when both trigger events and screen keys are set. ALL = show only if the event fires while the user is on one of the selected screens.
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Stack spacing={1.5}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Box>
                                            <Typography variant="subtitle1">Context filters (optional)</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Free-form params from events/screens (e.g. match_id). All filters must match (AND). Leave empty for no restriction.
                                            </Typography>
                                        </Box>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => setForm({
                                                ...form,
                                                contextFilters: [...form.contextFilters, emptyFilter()],
                                            })}
                                        >
                                            Add filter
                                        </Button>
                                    </Stack>
                                    {form.contextFilters.map((filter, index) => (
                                        <Card key={`ctx-${index}`} variant="outlined">
                                            <CardContent>
                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid item xs={12} md={3}>
                                                        <Autocomplete
                                                            freeSolo
                                                            options={contextKeyOptions}
                                                            value={filter.key}
                                                            onChange={(_, value) => {
                                                                const next = [...form.contextFilters];
                                                                next[index] = { ...filter, key: String(value ?? '') };
                                                                setForm({ ...form, contextFilters: next });
                                                            }}
                                                            onInputChange={(_, value) => {
                                                                const next = [...form.contextFilters];
                                                                next[index] = { ...filter, key: value };
                                                                setForm({ ...form, contextFilters: next });
                                                            }}
                                                            renderInput={(params) => <TextField {...params} label="Param key" />}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={2}>
                                                        <FormControl fullWidth>
                                                            <InputLabel>Op</InputLabel>
                                                            <Select
                                                                label="Op"
                                                                value={filter.op}
                                                                onChange={e => {
                                                                    const next = [...form.contextFilters];
                                                                    next[index] = {
                                                                        ...filter,
                                                                        op: e.target.value as RewardedPromptContextFilterOp,
                                                                    };
                                                                    setForm({ ...form, contextFilters: next });
                                                                }}
                                                            >
                                                                {CONTEXT_FILTER_OPS.map(op => (
                                                                    <MenuItem key={op} value={op}>{op}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <Autocomplete
                                                            multiple
                                                            freeSolo
                                                            disabled={filter.op === 'EXISTS'}
                                                            options={[]}
                                                            value={filter.values}
                                                            onChange={(_, value) => {
                                                                const next = [...form.contextFilters];
                                                                next[index] = {
                                                                    ...filter,
                                                                    values: value.map(String),
                                                                };
                                                                setForm({ ...form, contextFilters: next });
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label={filter.op === 'IN' ? 'Values' : 'Value'}
                                                                    helperText={filter.op === 'EXISTS' ? 'No value needed' : 'Press Enter to add'}
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} md={1}>
                                                        <IconButton
                                                            aria-label="Remove filter"
                                                            onClick={() => setForm({
                                                                ...form,
                                                                contextFilters: form.contextFilters.filter((_, i) => i !== index),
                                                            })}
                                                        >
                                                            ×
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    multiple
                                    options={userTags.map(t => t.tagName)}
                                    value={form.userTags}
                                    onChange={(_, value) => setForm({ ...form, userTags: value })}
                                    renderInput={(params) => <TextField {...params} label="User tags" />}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Tour completion"
                                    value={form.tourCompletion}
                                    onChange={e => setForm({ ...form, tourCompletion: e.target.value })}
                                    helperText="any | completed | skipped | not_completed | not_seen"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    multiple
                                    options={['android', 'ios']}
                                    value={form.osTypes}
                                    onChange={(_, value) => setForm({ ...form, osTypes: value })}
                                    renderInput={(params) => <TextField {...params} label="OS types" />}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <BuildNumberAutocomplete
                                    label="Min build"
                                    value={form.minBuild ?? null}
                                    versions={appVersions}
                                    loading={appVersionsLoading}
                                    onChange={value => setForm({ ...form, minBuild: value })}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <BuildNumberAutocomplete
                                    label="Max build"
                                    value={form.maxBuild ?? null}
                                    versions={appVersions}
                                    loading={appVersionsLoading}
                                    onChange={value => setForm({ ...form, maxBuild: value })}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Frequency mode</InputLabel>
                                    <Select
                                        label="Frequency mode"
                                        value={form.frequencyMode}
                                        onChange={e => setForm({ ...form, frequencyMode: e.target.value as RewardedPromptFrequencyMode })}
                                    >
                                        {FREQUENCY_MODES.map(mode => (
                                            <MenuItem key={mode} value={mode}>{mode}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Max dialogs / day (1–5)"
                                    value={form.maxImpressionsPerDay}
                                    onChange={e => setForm({ ...form, maxImpressionsPerDay: Number(e.target.value) || 1 })}
                                    inputProps={{ min: 1, max: 5 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Cooldown minutes"
                                    value={form.cooldownMinutes}
                                    onChange={e => setForm({ ...form, cooldownMinutes: Number(e.target.value) || 0 })}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Max impressions / user (lifetime)"
                                    value={form.maxImpressionsPerUser}
                                    onChange={e => setForm({ ...form, maxImpressionsPerUser: Number(e.target.value) || 1 })}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="Start at"
                                    type="datetime-local"
                                    InputLabelProps={{ shrink: true }}
                                    value={fromIsoToLocalInput(form.startAt)}
                                    onChange={e => setForm({ ...form, startAt: e.target.value ? new Date(e.target.value).toISOString() : null })}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    fullWidth
                                    label="End at"
                                    type="datetime-local"
                                    InputLabelProps={{ shrink: true }}
                                    value={fromIsoToLocalInput(form.endAt)}
                                    onChange={e => setForm({ ...form, endAt: e.target.value ? new Date(e.target.value).toISOString() : null })}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                    <CardActions>
                        <Button variant="contained" disabled={saving} onClick={handleSave}>
                            {saving ? 'Saving…' : 'Save'}
                        </Button>
                    </CardActions>
                </Card>
            </Stack>

            <Snackbar open={toast != null} autoHideDuration={4000} onClose={() => setToast(null)}>
                <Alert severity={toast?.severity ?? 'success'} onClose={() => setToast(null)}>
                    {toast?.message ?? ''}
                </Alert>
            </Snackbar>
        </Box>
    );
}
